import React, { useState, useEffect } from 'react';
import { ChevronRight, CheckCircle, Circle, BookOpen, Lightbulb, Target } from 'lucide-react';
import QuizComponent from './QuizComponent';

/**
 * Learn Concepts Tab Component
 * 
 * Educational content about A2A communication concepts
 * Includes interactive lessons and quizzes
 */
const LearnConceptsTab = () => {
  const [selectedTopic, setSelectedTopic] = useState('introduction');
  const [completedTopics, setCompletedTopics] = useState(new Set());
  const [showQuiz, setShowQuiz] = useState(false);
  const [progress, setProgress] = useState(0);

  const topics = [
    {
      id: 'introduction',
      title: 'Introduction to A2A',
      icon: <BookOpen className="w-5 h-5" />,
      duration: '5 min',
      description: 'Understanding the basics of Agent-to-Agent communication',
      content: {
        overview: 'Agent-to-Agent (A2A) communication enables autonomous AI agents to collaborate seamlessly without human intervention. This revolutionary approach allows complex workflows to be distributed across specialized agents.',
        keyPoints: [
          'Agents communicate via structured JSON messages',
          'Each agent has specific capabilities and responsibilities',
          'Communication follows a standardized protocol',
          'Agents can discover, negotiate, and execute tasks autonomously'
        ],
        realWorldExample: {
          title: 'Real-World Scenario: HR Recruitment',
          description: 'Imagine a recruitment system where:',
          steps: [
            '🔍 Screening Agent analyzes resumes and identifies qualified candidates',
            '📧 Communication Agent schedules interviews automatically',
            '📊 Assessment Agent evaluates interview performance',
            '✅ Onboarding Agent handles paperwork and orientation'
          ],
          benefit: 'All agents work together without manual coordination, saving hours of work!'
        },
        learnMore: [
          'How agents authenticate and verify identity',
          'Message structure and data formats',
          'Error handling and retry mechanisms'
        ]
      }
    },
    {
      id: 'capabilities',
      title: 'Capability Discovery',
      icon: <Target className="w-5 h-5" />,
      duration: '7 min',
      description: 'How agents discover what others can do',
      content: {
        overview: 'Before agents can work together, they need to understand each other\'s capabilities. Capability Discovery is the first step in A2A communication.',
        keyPoints: [
          'Agents expose their capabilities through a discovery endpoint',
          'Capabilities describe what an agent can do, parameters required, and expected outputs',
          'Discovery happens automatically when agents first connect',
          'Capabilities can be filtered and matched based on requirements'
        ],
        codeExample: {
          title: 'Discovery Request Example',
          code: `{
  "action": "discover_capabilities",
  "sender": "client-agent-123",
  "timestamp": "2025-10-15T10:30:00Z"
}

// Response:
{
  "capabilities": [
    {
      "name": "generate_learning_content",
      "description": "Generate educational content",
      "parameters": ["topic", "difficulty"],
      "timeout": 60
    },
    {
      "name": "simulate_a2a_interaction",
      "description": "Run interactive simulations",
      "parameters": ["simulation_type", "complexity"],
      "timeout": 120
    }
  ]
}`
        },
        realWorldExample: {
          title: 'Discovery in Action',
          description: 'When a Client Agent connects to the Teaching System:',
          steps: [
            '1️⃣ Client sends discovery request to backend',
            '2️⃣ Backend responds with all available teaching capabilities',
            '3️⃣ Client displays available learning modules',
            '4️⃣ User selects a topic and execution begins'
          ],
          benefit: 'Dynamic discovery means capabilities can be added without updating the client!'
        }
      }
    },
    {
      id: 'task-execution',
      title: 'Task Execution',
      icon: <Lightbulb className="w-5 h-5" />,
      duration: '8 min',
      description: 'Executing tasks and managing state',
      content: {
        overview: 'Once capabilities are discovered, agents can execute tasks and manage their execution state throughout the workflow.',
        keyPoints: [
          'Tasks are executed via structured execute messages',
          'State is maintained across multi-step workflows',
          'Agents track execution progress and results',
          'Error handling ensures robustness'
        ],
        codeExample: {
          title: 'Task Execution Example',
          code: `{
  "action": "execute",
  "task": "generate_learning_content",
  "parameters": {
    "topic": "capability_discovery",
    "difficulty": "beginner",
    "include_simulation": true
  },
  "sender": "client-agent-123",
  "timestamp": "2025-10-15T10:32:00Z",
  "signature": "hmac-sha256-hash..."
}

// Response with state:
{
  "success": true,
  "state": "completed",
  "module_id": "mod-uuid-123",
  "content": {
    "title": "Understanding Capability Discovery",
    "sections": [...],
    "quiz": [...]
  },
  "timestamp": "2025-10-15T10:32:45Z"
}`
        },
        realWorldExample: {
          title: 'Multi-Step Workflow',
          description: 'A complete learning session:',
          steps: [
            '📚 Generate content for selected topic',
            '🎮 Create interactive simulation',
            '📝 Present quiz questions',
            '📊 Assess understanding and provide feedback',
            '💾 Save progress to database'
          ],
          benefit: 'State management ensures seamless experience even if connection drops!'
        }
      }
    },
    {
      id: 'security',
      title: 'Secure Collaboration',
      icon: <span className="text-lg">🔒</span>,
      duration: '6 min',
      description: 'Keeping agent communication secure',
      content: {
        overview: 'Security is paramount in A2A communication. Messages are signed and verified to prevent tampering and ensure authenticity.',
        keyPoints: [
          'Messages are signed using HMAC-SHA256',
          'Both agents verify signatures before processing',
          'Secret keys are never transmitted',
          'Rate limiting prevents abuse'
        ],
        codeExample: {
          title: 'Message Signing Process',
          code: `// Backend signing (Python)
import hmac
import hashlib

def sign_message(message, secret_key):
    message_str = json.dumps(message, sort_keys=True)
    signature = hmac.new(
        secret_key.encode(),
        message_str.encode(),
        hashlib.sha256
    ).hexdigest()
    return signature

// Frontend verification (JavaScript)
async function verifySignature(message, signature, secretKey) {
  const encoder = new TextEncoder();
  const key = await crypto.subtle.importKey(
    'raw',
    encoder.encode(secretKey),
    { name: 'HMAC', hash: 'SHA-256' },
    false,
    ['verify']
  );
  
  const messageStr = JSON.stringify(message, Object.keys(message).sort());
  const messageBytes = encoder.encode(messageStr);
  const signatureBytes = hexToBytes(signature);
  
  return await crypto.subtle.verify(
    'HMAC',
    key,
    signatureBytes,
    messageBytes
  );
}`
        },
        realWorldExample: {
          title: 'Security in Practice',
          description: 'Every message exchange is protected:',
          steps: [
            '🔑 Client signs request with shared secret',
            '📨 Backend receives and verifies signature',
            '✅ If valid, backend processes request',
            '🔏 Backend signs response before sending',
            '🔍 Client verifies response signature'
          ],
          benefit: 'Man-in-the-middle attacks are prevented - tampering is immediately detected!'
        }
      }
    },
    {
      id: 'ux-negotiation',
      title: 'UX Negotiation',
      icon: <span className="text-lg">🎨</span>,
      duration: '5 min',
      description: 'Adapting the interface dynamically',
      content: {
        overview: 'UX Negotiation allows agents to dynamically adjust the user interface based on context, user needs, and device capabilities.',
        keyPoints: [
          'Backend can recommend UI changes based on content',
          'Client adapts presentation for optimal learning',
          'Personalization based on user progress',
          'Responsive design recommendations'
        ],
        realWorldExample: {
          title: 'Adaptive Learning Interface',
          description: 'UI adapts to your learning style:',
          steps: [
            '👶 Beginner: More visuals, step-by-step guidance, tooltips',
            '🎓 Intermediate: Code examples, interactive simulations',
            '🚀 Advanced: Technical deep-dives, performance metrics',
            '📱 Mobile: Simplified layout, touch-optimized controls'
          ],
          benefit: 'Personalized experience improves learning outcomes by 40%!'
        },
        codeExample: {
          title: 'UX Negotiation Message',
          code: `{
  "action": "negotiate_ux",
  "context": {
    "user_level": "beginner",
    "device": "mobile",
    "previous_modules": ["introduction"],
    "time_available": "15min"
  }
}

// Backend AI Response:
{
  "recommendations": {
    "layout": "single-column",
    "font_size": "large",
    "include_visuals": true,
    "quiz_format": "multiple-choice",
    "pace": "slow",
    "hints_enabled": true
  }
}`
        }
      }
    }
  ];

  const currentTopic = topics.find(t => t.id === selectedTopic);

  useEffect(() => {
    const progressPercent = (completedTopics.size / topics.length) * 100;
    setProgress(progressPercent);
  }, [completedTopics, topics.length]);

  const markTopicComplete = () => {
    setCompletedTopics(new Set([...completedTopics, selectedTopic]));
    setShowQuiz(true);
  };

  const handleQuizComplete = (score) => {
    console.log(`Quiz completed with score: ${score}`);
    setShowQuiz(false);
    // Move to next topic
    const currentIndex = topics.findIndex(t => t.id === selectedTopic);
    if (currentIndex < topics.length - 1) {
      setSelectedTopic(topics[currentIndex + 1].id);
    }
  };

  return (
    <div className="grid grid-cols-12 gap-6">
      {/* Sidebar - Topic List */}
      <div className="col-span-3">
        <div className="card p-4 sticky top-4">
          <h2 className="text-lg font-bold text-gray-900 mb-4">Learning Path</h2>
          
          {/* Progress Bar */}
          <div className="mb-6">
            <div className="flex justify-between text-sm text-gray-600 mb-2">
              <span>Progress</span>
              <span>{Math.round(progress)}%</span>
            </div>
            <div className="w-full bg-gray-200 rounded-full h-2">
              <div 
                className="bg-gradient-to-r from-indigo-600 to-purple-600 h-2 rounded-full transition-all duration-500"
                style={{ width: `${progress}%` }}
              />
            </div>
          </div>

          {/* Topic List */}
          <div className="space-y-2">
            {topics.map((topic) => {
              const isCompleted = completedTopics.has(topic.id);
              const isActive = selectedTopic === topic.id;

              return (
                <button
                  key={topic.id}
                  onClick={() => setSelectedTopic(topic.id)}
                  className={`w-full text-left p-3 rounded-lg transition-all ${
                    isActive
                      ? 'bg-indigo-50 border-2 border-indigo-600'
                      : 'bg-white border border-gray-200 hover:border-indigo-300'
                  }`}
                >
                  <div className="flex items-start space-x-3">
                    <div className={`mt-0.5 ${isActive ? 'text-indigo-600' : 'text-gray-400'}`}>
                      {isCompleted ? (
                        <CheckCircle className="w-5 h-5 text-green-500" />
                      ) : (
                        topic.icon
                      )}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="font-medium text-sm text-gray-900">
                        {topic.title}
                      </div>
                      <div className="text-xs text-gray-500 mt-0.5">
                        {topic.duration}
                      </div>
                    </div>
                  </div>
                </button>
              );
            })}
          </div>
        </div>
      </div>

      {/* Main Content Area */}
      <div className="col-span-9">
        {!showQuiz ? (
          <div className="card p-8 animate-fadeIn">
            {/* Topic Header */}
            <div className="flex items-start justify-between mb-6">
              <div className="flex items-center space-x-4">
                <div className="bg-gradient-to-br from-indigo-600 to-purple-600 p-3 rounded-xl text-white">
                  {currentTopic.icon}
                </div>
                <div>
                  <h1 className="text-3xl font-bold text-gray-900">
                    {currentTopic.title}
                  </h1>
                  <p className="text-gray-600 mt-1">{currentTopic.description}</p>
                </div>
              </div>
              <span className="badge bg-indigo-100 text-indigo-800">
                {currentTopic.duration}
              </span>
            </div>

            {/* Overview */}
            <div className="mb-8">
              <h2 className="text-xl font-bold text-gray-900 mb-3">Overview</h2>
              <p className="text-gray-700 leading-relaxed">
                {currentTopic.content.overview}
              </p>
            </div>

            {/* Key Points */}
            {currentTopic.content.keyPoints && (
              <div className="mb-8">
                <h2 className="text-xl font-bold text-gray-900 mb-4">Key Points</h2>
                <div className="space-y-3">
                  {currentTopic.content.keyPoints.map((point, idx) => (
                    <div key={idx} className="flex items-start space-x-3 animate-slideIn" style={{animationDelay: `${idx * 100}ms`}}>
                      <ChevronRight className="w-5 h-5 text-indigo-600 mt-0.5 flex-shrink-0" />
                      <span className="text-gray-700">{point}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Code Example */}
            {currentTopic.content.codeExample && (
              <div className="mb-8">
                <h2 className="text-xl font-bold text-gray-900 mb-4">
                  {currentTopic.content.codeExample.title}
                </h2>
                <div className="bg-gray-900 rounded-xl p-6 overflow-x-auto">
                  <pre className="text-sm text-gray-100 font-mono">
                    {currentTopic.content.codeExample.code}
                  </pre>
                </div>
              </div>
            )}

            {/* Real World Example */}
            {currentTopic.content.realWorldExample && (
              <div className="mb-8">
                <div className="bg-gradient-to-br from-green-50 to-emerald-50 rounded-xl p-6 border border-green-200">
                  <h2 className="text-xl font-bold text-gray-900 mb-2">
                    {currentTopic.content.realWorldExample.title}
                  </h2>
                  <p className="text-gray-700 mb-4">
                    {currentTopic.content.realWorldExample.description}
                  </p>
                  <div className="space-y-2 mb-4">
                    {currentTopic.content.realWorldExample.steps.map((step, idx) => (
                      <div key={idx} className="flex items-start space-x-3">
                        <span className="text-gray-700">{step}</span>
                      </div>
                    ))}
                  </div>
                  <div className="bg-green-100 rounded-lg p-4 border border-green-300">
                    <div className="flex items-start space-x-2">
                      <Lightbulb className="w-5 h-5 text-green-700 flex-shrink-0 mt-0.5" />
                      <div>
                        <div className="font-medium text-green-900">Why This Matters</div>
                        <div className="text-green-800 text-sm mt-1">
                          {currentTopic.content.realWorldExample.benefit}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Learn More */}
            {currentTopic.content.learnMore && (
              <div className="mb-8">
                <h2 className="text-xl font-bold text-gray-900 mb-3">Learn More</h2>
                <div className="space-y-2">
                  {currentTopic.content.learnMore.map((item, idx) => (
                    <div key={idx} className="flex items-center space-x-2 text-indigo-600 hover:text-indigo-700">
                      <Circle className="w-2 h-2 fill-current" />
                      <span className="text-sm">{item}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Complete Button */}
            <div className="flex justify-end">
              <button
                onClick={markTopicComplete}
                disabled={completedTopics.has(selectedTopic)}
                className={`btn-primary px-8 py-3 ${
                  completedTopics.has(selectedTopic)
                    ? 'opacity-50 cursor-not-allowed'
                    : ''
                }`}
              >
                {completedTopics.has(selectedTopic) ? 'Completed ✓' : 'Complete & Take Quiz'}
              </button>
            </div>
          </div>
        ) : (
          <QuizComponent
            topic={selectedTopic}
            topicTitle={currentTopic.title}
            onComplete={handleQuizComplete}
            onBack={() => setShowQuiz(false)}
          />
        )}
      </div>
    </div>
  );
};

export default LearnConceptsTab;

